import React, { useMemo } from "react";
import Card from "components/Card";
import CardHeader from "components/CardHeader";
import {
  BillOfSaleContainer,
  BillItemContainer,
  ItemContainer,
} from "./styles";
import moment from "moment-timezone";
import { HST_RATE, MAT_RATE, TIME_ZONE } from "constants/constant";
import { dateDiff } from "utils/util";

const BookingBreakdown = ({
  checkInDate,
  checkOutDate,
  roomQuantity,
  roomType,
  ratePlan,
  coupon,
  breakfastTotal,
  breakfastUnitPrice,
  parkingTotal,
  parkingUnitPrice,
}) => {
  const nights = dateDiff(checkInDate, checkOutDate);
  const originalRoomFee = roomType.pricePerNight * roomQuantity * nights || 0;
  const ratePlanValue =
    ratePlan.rateChangeType === "decrease_by_percent"
      ? (originalRoomFee * ratePlan.rateChangeValue) / 100
      : ratePlan.rateChangeValue || 0;
  const roomRate = originalRoomFee - ratePlanValue;
  const couponValue =
    coupon.rateType === "FIXED_RATE"
      ? coupon.discount
      : (roomRate * coupon.discount) / 100 || 0;
  const roomFee = roomRate - couponValue;
  const MAT = roomFee * MAT_RATE;
  const addOnFee =
    breakfastTotal * breakfastUnitPrice + parkingTotal * parkingUnitPrice;
  const subtotal = roomFee + MAT + addOnFee;
  const HST = subtotal * HST_RATE;
  const total = subtotal + HST;

  const billItems = useMemo(() => {
    const items = [];

    // Rooms per night
    if (roomType && roomQuantity) {
      for (
        let i = moment(checkInDate);
        i.isBefore(moment(checkOutDate));
        i.add(1, "days")
      ) {
        for (let q = 0; q < roomQuantity; q++) {
          items.push(
            <ItemContainer
              key={`room-${i.format("YYYYMMDD")}-${q}`}
            >
              <div className="description">
                {`${roomType.name} - ${roomType.bed || "Queen bed"} ${
                  roomType.hasWindows
                    ? "w/ window"
                    : roomType.hasSkylight
                    ? "w/ skylight"
                    : ""
                } (${moment(i).tz(TIME_ZONE).format("MMM Do YYYY")})`}
              </div>
              <div className="price normal">
                <span className="amount">
                  {"$" + roomType.pricePerNight.toFixed(2)}
                </span>
              </div>
            </ItemContainer>
          );
        }
      }
    }

    // Rate plan
    if (Object.keys(ratePlan).length) {
      items.push(
        <ItemContainer key="rate-plan" className="ratePlan">
          <div className="description warning">{ratePlan.title}</div>
          <div className="price warning">
            <span className="amount">{"-$" + ratePlanValue.toFixed(2)}</span>
          </div>
        </ItemContainer>
      );
    }

    // Coupon
    if (Object.keys(coupon).length) {
      items.push(
        <ItemContainer key="coupon" className="coupon">
          <div className="description warning">
            {`Coupon Code (${coupon.couponCode})`}
          </div>
          <div className="price warning">
            <span className="amount">{"-$" + couponValue.toFixed(2)}</span>
          </div>
        </ItemContainer>
      );
    }

    // Breakfast
    for (let i = 0; i < breakfastTotal; i++) {
      items.push(
        <ItemContainer key={`breakfast-${i}`}>
          <div className="description">Breakfast</div>
          <div className="price normal">
            <span className="amount">
              {"$" + breakfastUnitPrice.toFixed(2)}
            </span>
          </div>
        </ItemContainer>
      );
    }

    // Parking
    for (let i = 0; i < parkingTotal; i++) {
      items.push(
        <ItemContainer key={`parking-${i}`}>
          <div className="description">Parking</div>
          <div className="price normal">
            <span className="amount">
              {"$" + parkingUnitPrice.toFixed(2)}
            </span>
          </div>
        </ItemContainer>
      );
    }

    return items;
  }, [
    checkInDate,
    checkOutDate,
    roomQuantity,
    roomType,
    ratePlan,
    coupon,
    breakfastTotal,
    parkingTotal,
  ]);

  return (
    <Card
      additionalstyle={`
          
        @media only screen and (max-width: 767px) {

          .type {
            width: 100%;
            margin-bottom: 10px; 
          }

          .buttonContainer {
            width: 100%;
            justify-content: space-between;
          }
          
          flex-direction: column;
          justify-content: center;
        }          
      `}
    >
      <CardHeader>
        <div className="type">Booking Breakdown</div>
      </CardHeader>
      <BillItemContainer>
        <div className="title">BOOKING BREAKDOWN</div>
        <ItemContainer>
          <div className="transaction header">TRANSACTION</div>
          <div className="status header"></div>
          <div className="action header"></div>
          <div className="price header">PRICE</div>
        </ItemContainer>
        {billItems}
      </BillItemContainer>
      <BillOfSaleContainer>
        <div className="MAT">
          <div className="title">{`Municipal Accommodation Tax (${
            MAT_RATE * 100
          }%)`}</div>
          <div className="price normal">
            <span className="amount">{"$" + (MAT.toFixed(2) || "0.00")}</span>
          </div>
        </div>
        <div className="HST">
          <div className="title">{`HST (${HST_RATE * 100}%)`}</div>
          <div className="price normal">
            <span className="amount">{"$" + (HST.toFixed(2) || "0.00")}</span>
          </div>
        </div>
        <div className="total">
          <div className="title">TOTAL</div>
          <div className="price normal">
            <span className="amount">{"$" + (total.toFixed(2) || "0.00")}</span>
          </div>
        </div>
      </BillOfSaleContainer>
    </Card>
  );
};

export default BookingBreakdown;
